import type { CSSProperties } from 'react';

import { AppColors } from '@/config/appColors';
import { AppStyles } from '@/config/appStyles';
import type { L10n } from '@/l10n/types';
import { tryGetL10n } from '@/l10n/useL10n';
import { DiagnosticsLog } from '@/utils/diagnostics/diagnosticsLog';
import {
  AppException,
  appExceptionTypeFormattedName,
  currentStackTrace,
} from '@/utils/exceptions/appException';
import type { AppFailure } from '@/utils/exceptions/appFailure';
import { FailureCatalog } from '@/utils/exceptions/failureCatalog';
import { NetworkStatus } from '@/utils/networkStatus';

/**
 * Перенос `lib/src/presentation/widgets/snackbar/snackbar_manager.dart`.
 *
 * В Dart тосты рисовал пакет `toastification`: менеджер был синглтоном без
 * состояния и только дёргал `toastification.show`. В вебе пакета нет, поэтому
 * очередь тостов живёт здесь — менеджер хранит список и оповещает
 * подписчиков, а рисует их `SnackbarHost` порталом в `document.body`.
 * Публичный API остался прежним, чтобы вызовы из контроллеров и страниц
 * переносились дословно: `SnackbarManager.instance.showFailure(failure)`.
 *
 * Длительности — миллисекунды вместо `Duration`; `null` значит «не
 * закрывать самому» (в Dart это `autoCloseDuration: null`).
 */

/** `Duration(seconds: 2)` */
const DEFAULT_CLOSE_DURATION_MS = 2000;

/**
 * Сколько висит тост с кнопкой «Повторить».
 *
 * За две секунды нажать невозможно, поэтому для таких тостов время другое.
 */
const RETRY_CLOSE_DURATION_MS = 8000;

/**
 * Длительность анимации ухода. Должна совпадать с `--snackbar-duration`
 * в `SnackbarHost.module.css`: тост убирается из списка после неё, иначе
 * он либо исчезнет рывком, либо задержится пустым местом.
 */
export const SNACKBAR_EXIT_DURATION_MS = 300;

/** Общая ссылка на пустой список: снимок не меняется без причины. */
const EMPTY_ITEMS: readonly SnackbarItem[] = [];

/** Перенос `enum SnackbarType`. */
export const SnackbarType = {
  success: 'success',
  error: 'error',
  warning: 'warning',
} as const;

export type SnackbarType = (typeof SnackbarType)[keyof typeof SnackbarType];

/** Цвет, который в Dart был полем перечисления (`SnackbarType(this.color)`). */
export const SNACKBAR_TYPE_COLOR: Record<SnackbarType, string> = {
  [SnackbarType.success]: AppColors.c91D75B,
  [SnackbarType.error]: AppColors.cD75B5B,
  [SnackbarType.warning]: AppColors.cD7B25B,
};

/** `SnackbarType.defaultTitle(context)` */
export function snackbarTypeDefaultTitle(type: SnackbarType, l10n: L10n): string {
  switch (type) {
    case SnackbarType.success:
      return l10n.success;
    case SnackbarType.error:
      return l10n.error;
    case SnackbarType.warning:
      return l10n.warning;
  }
}

/** `SnackbarType.titleStyle` */
export function snackbarTypeTitleStyle(type: SnackbarType): CSSProperties {
  return { ...AppStyles.s20w600, color: SNACKBAR_TYPE_COLOR[type] };
}

/** `SnackbarType.descriptionStyle` — цвет один для всех типов. */
export function snackbarTypeDescriptionStyle(): CSSProperties {
  return { ...AppStyles.s20w400, color: '#fff' };
}

/**
 * Замена `ToastificationItem`: `show` возвращает его, а `app_state_provider`
 * держит ссылку, чтобы закрыть тост «идёт обновление» своими руками.
 */
export interface SnackbarItem {
  readonly id: string;
  readonly type: SnackbarType;
  readonly title?: string;
  readonly description?: string;
  readonly code?: string;
  readonly onRetry?: () => void;
  /** `null` — тост не закрывается сам. */
  readonly autoCloseDuration: number | null;
  /** Идёт анимация ухода: элемент ещё в списке, но уже уезжает. */
  readonly leaving: boolean;
}

/** Аргументы `showSuccess` / `showError` / `showWarning`. */
export interface SnackbarSimpleOptions {
  readonly title?: string;
  readonly description?: string;
  readonly autoCloseDuration?: number | null;
}

export interface SnackbarFailureOptions {
  readonly onRetry?: () => void;
  readonly autoCloseDuration?: number | null;
}

export interface SnackbarHandleErrorOptions {
  readonly stackTrace?: string;
  readonly title?: string;
  readonly description?: string;
  readonly autoCloseDuration?: number | null;
}

export interface SnackbarShowOptions {
  readonly type: SnackbarType;
  readonly title?: string;
  readonly description?: string;
  readonly code?: string;
  readonly onRetry?: () => void;
  readonly autoCloseDuration?: number | null;
  /** false — сбой уже записан в журнал вместе с диагностикой. */
  readonly logToast?: boolean;
}

export interface SnackbarDismissOptions {
  readonly showRemoveAnimation?: boolean;
}

export class SnackbarManager {
  private constructor() {}

  static readonly defaultCloseDuration = DEFAULT_CLOSE_DURATION_MS;

  static readonly retryCloseDuration = RETRY_CLOSE_DURATION_MS;

  static readonly instance = new SnackbarManager();

  #items: readonly SnackbarItem[] = EMPTY_ITEMS;

  #nextId = 0;

  readonly #listeners = new Set<() => void>();

  /** Таймеры автозакрытия и ухода — по одному на тост. */
  readonly #timers = new Map<string, number>();

  /** Подписка для `useSyncExternalStore` в `SnackbarHost`. */
  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };

  getSnapshot = (): readonly SnackbarItem[] => this.#items;

  showSuccess({
    title,
    description,
    autoCloseDuration = DEFAULT_CLOSE_DURATION_MS,
  }: SnackbarSimpleOptions = {}): SnackbarItem {
    return this.show({
      type: SnackbarType.success,
      title,
      autoCloseDuration,
      description,
    });
  }

  showError({
    title,
    description,
    autoCloseDuration = DEFAULT_CLOSE_DURATION_MS,
  }: SnackbarSimpleOptions = {}): SnackbarItem {
    return this.show({
      type: SnackbarType.error,
      title,
      autoCloseDuration,
      description,
    });
  }

  showWarning({
    title,
    description,
    autoCloseDuration = DEFAULT_CLOSE_DURATION_MS,
  }: SnackbarSimpleOptions = {}): SnackbarItem {
    return this.show({
      autoCloseDuration,
      type: SnackbarType.warning,
      title,
      description,
    });
  }

  /**
   * Показывает сбой так, как его должен видеть посетитель: человеческая фраза
   * и короткий код для справочника. Технический текст уходит только в лог —
   * раньше на экран киоска попадал весь DioException вместе с адресом сервера.
   */
  showFailure(
    failure: AppFailure,
    { onRetry, autoCloseDuration }: SnackbarFailureOptions = {},
  ): SnackbarItem {
    // Без сети на самом планшете сетевой сбой показывается как «проверьте
    // Wi-Fi», а не как «сервер недоступен»; в лог уходят оба кода.
    const shown = failure.presented({ offline: NetworkStatus.instance.isOffline });
    logFailure(failure, shown);

    const l10n = tryGetL10n();
    const retry =
      onRetry !== undefined && FailureCatalog.canRetry(shown) ? onRetry : undefined;
    const title = l10n === null ? undefined : FailureCatalog.message(shown, l10n);
    DiagnosticsLog.instance.failure(failure, { shown, message: title });

    return this.show({
      type: SnackbarType.error,
      title,
      code: shown.code,
      onRetry: retry,
      autoCloseDuration:
        autoCloseDuration ??
        (retry !== undefined ? RETRY_CLOSE_DURATION_MS : DEFAULT_CLOSE_DURATION_MS),
      logToast: false,
    });
  }

  /**
   * Обёртка для мест, где операция ещё не размечена.
   *
   * Постепенно заменяется на [showFailure] с указанием операции.
   */
  handleError(
    error: unknown,
    {
      stackTrace,
      title,
      description,
      autoCloseDuration = DEFAULT_CLOSE_DURATION_MS,
    }: SnackbarHandleErrorOptions = {},
  ): SnackbarItem {
    const appException =
      error instanceof AppException
        ? error
        : AppException.handle(error, stackTrace ?? currentStackTrace());
    return this.show({
      autoCloseDuration,
      type: SnackbarType.error,
      title: title ?? appExceptionTypeFormattedName(appException.type),
      description:
        description ?? appException.apiError?.message ?? String(appException.innerError),
    });
  }

  /**
   * `toastification.dismiss(item, showRemoveAnimation: …)`.
   *
   * С анимацией тост сначала помечается уходящим (CSS-класс в хосте) и
   * убирается из списка через [SNACKBAR_EXIT_DURATION_MS].
   */
  dismiss(item: SnackbarItem, { showRemoveAnimation = true }: SnackbarDismissOptions = {}): void {
    const current = this.#items.find((candidate) => candidate.id === item.id);
    if (current === undefined || current.leaving) return;
    this.#clearTimer(item.id);
    if (!showRemoveAnimation) {
      this.#remove(item.id);
      return;
    }
    this.#setItems(
      this.#items.map((candidate) =>
        candidate.id === item.id ? { ...candidate, leaving: true } : candidate,
      ),
    );
    this.#timers.set(
      item.id,
      window.setTimeout(() => this.#remove(item.id), SNACKBAR_EXIT_DURATION_MS),
    );
  }

  show({
    type,
    title,
    description,
    code,
    onRetry,
    autoCloseDuration = DEFAULT_CLOSE_DURATION_MS,
    // false — сбой уже записан в журнал вместе с диагностикой.
    logToast = true,
  }: SnackbarShowOptions): SnackbarItem {
    if (logToast) {
      DiagnosticsLog.instance.toast({ type, title, code });
    }
    this.#nextId += 1;
    // Оформление тоста (padding: zero, borderRadius 16, фон white 0.4,
    // borderSide.none, кнопка закрытия, style: simple) в Dart задавалось
    // прямо здесь — в вебе оно живёт в `SnackbarHost.module.css`.
    const item: SnackbarItem = {
      id: `snackbar-${this.#nextId}`,
      type,
      title,
      description,
      code,
      onRetry,
      autoCloseDuration,
      leaving: false,
    };
    this.#setItems([...this.#items, item]);
    this.#scheduleAutoClose(item);
    return item;
  }

  #scheduleAutoClose(item: SnackbarItem): void {
    const duration = item.autoCloseDuration;
    if (duration === null) return;
    this.#timers.set(
      item.id,
      window.setTimeout(() => this.dismiss(item), duration),
    );
  }

  #clearTimer(id: string): void {
    const timer = this.#timers.get(id);
    if (timer === undefined) return;
    window.clearTimeout(timer);
    this.#timers.delete(id);
  }

  #remove(id: string): void {
    this.#clearTimer(id);
    const next = this.#items.filter((candidate) => candidate.id !== id);
    if (next.length === this.#items.length) return;
    this.#setItems(next.length === 0 ? EMPTY_ITEMS : next);
  }

  #setItems(next: readonly SnackbarItem[]): void {
    this.#items = next;
    for (const listener of [...this.#listeners]) {
      listener();
    }
  }
}

/**
 * `dev.log(…, name: 'Failure', error: …, stackTrace: …)` из Dart.
 *
 * В браузере отдельного канала логов нет, поэтому та же строка уходит в
 * `console.debug`: она нужна инженеру в консоли, а не посетителю на экране.
 */
function logFailure(failure: AppFailure, shown: AppFailure): void {
  const message =
    shown === failure
      ? failure.diagnostics
      : `${shown.code} (${failure.code}) · ${failure.diagnostics}`;
  const details = [failure.cause, failure.stackTrace].filter(
    (part) => part !== null && part !== undefined,
  );
  console.debug(`[Failure] ${message}`, ...details);
}
